import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import { useBrand } from "../../context/BrandContext";
import {
  fetchTeCountRecords,
  saveTeCountRecord,
  deleteTeCountRecord,
} from "../../api/coachCountApi";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import {
  getStationCameraDeviceId,
  resolveStationCameraKey,
  setStationCameraDeviceId,
} from "../../utils/stationCameraBinding";
import "./LiveTrainingCoachChronoPanel.css";
import "./LiveTrainingCoachCountPanel.css";

function emptyCountState() {
  return {
    value: 0,
    savedValue: null,
    saving: false,
    deleting: false,
    dirty: false,
    error: null,
  };
}

/**
 * Coach counting panel — per-player counters (off-table reps, regularity
 * streaks, successful serves / multiball balls).
 */
export function LiveTrainingCoachCountPanel({
  open,
  onClose,
  teId,
  players = [],
  countLabel = "",
  eyebrow = null,
  tsgId = null,
  grId = null,
  tsId = null,
  socket = null,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";

  const stationKey = useMemo(
    () => resolveStationCameraKey({ tsgId, grId, teId }),
    [tsgId, grId, teId]
  );

  const [counters, setCounters] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [cameraDeviceId, setCameraDeviceId] = useState(() =>
    getStationCameraDeviceId(stationKey)
  );
  const playersRef = useRef(players);
  const countersRef = useRef({});
  const hasLoadedRef = useRef(false);
  const broadcastRef = useRef(() => {});

  playersRef.current = players;
  countersRef.current = counters;

  useEffect(() => {
    setCameraDeviceId(getStationCameraDeviceId(stationKey));
  }, [stationKey]);

  useEffect(() => {
    if (!open || !navigator?.mediaDevices?.enumerateDevices) return undefined;
    let cancelled = false;
    navigator.mediaDevices
      .enumerateDevices()
      .then((list) => {
        if (cancelled) return;
        setCameraDevices(list.filter((d) => d.kind === "videoinput"));
      })
      .catch(() => {
        if (!cancelled) setCameraDevices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const playerKeys = useMemo(
    () => (players || []).map((p) => p.key).join("|"),
    [players]
  );

  const drillTitle =
    (countLabel && String(countLabel).trim()) ||
    t("liveTrainingCountModeDefaultLabel") ||
    "Reps";

  useEffect(() => {
    if (!open) {
      hasLoadedRef.current = false;
      return;
    }
    setCounters((prev) => {
      const next = {};
      for (const p of playersRef.current || []) {
        next[p.key] = prev[p.key] || emptyCountState();
      }
      return next;
    });
  }, [open, playerKeys]);

  useEffect(() => {
    if (!open || !teId) return undefined;
    const ac = new AbortController();
    const initial = !hasLoadedRef.current;
    if (initial) setLoading(true);
    setLoadError(null);
    fetchTeCountRecords(teId, { signal: ac.signal })
      .then((data) => {
        const byUser = new Map();
        for (const rec of data?.records || []) {
          if (rec?.user_id != null) byUser.set(String(rec.user_id), rec);
        }
        setCounters((prev) => {
          const next = { ...prev };
          for (const p of playersRef.current || []) {
            const rec = (p.userId && byUser.get(String(p.userId))) || null;
            const saved =
              rec?.metric_value != null ? Number(rec.metric_value) : null;
            const cur = next[p.key] || emptyCountState();
            if (cur.dirty) {
              next[p.key] = {
                ...cur,
                savedValue:
                  saved != null && Number.isFinite(saved)
                    ? saved
                    : cur.savedValue,
              };
              continue;
            }
            next[p.key] = {
              ...cur,
              value:
                saved != null && Number.isFinite(saved) ? saved : cur.value,
              savedValue:
                saved != null && Number.isFinite(saved) ? saved : null,
            };
          }
          return next;
        });
        hasLoadedRef.current = true;
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setLoadError(err?.message || t("error") || "Error");
      })
      .finally(() => {
        if (initial) setLoading(false);
      });
    return () => ac.abort();
  }, [open, teId, playerKeys, t]);

  const broadcastCount = useCallback(
    (snapshot) => {
      if (!socket || !tsId || !teId) return;
      const rows = playersRef.current || [];
      socket.emit("offTableCountSync", {
        ts_id: tsId,
        te_id: teId,
        tsg_id: tsgId || null,
        countLabel: countLabel || null,
        players: rows.map((player) => {
          const row = snapshot?.[player.key] || emptyCountState();
          return {
            key: player.key,
            name: player.name,
            userId: player.userId || null,
            playerId: player.playerId || null,
            value: Math.max(0, Math.round(Number(row.value) || 0)),
            savedValue:
              row.savedValue != null ? Number(row.savedValue) : null,
            dirty: Boolean(row.dirty),
          };
        }),
      });
    },
    [socket, tsId, teId, tsgId, countLabel]
  );
  broadcastRef.current = broadcastCount;

  useEffect(() => {
    if (!open) return;
    if (!(playersRef.current || []).length) return;
    broadcastRef.current(countersRef.current);
  }, [open, playerKeys, teId, countLabel]);

  const bump = useCallback((key, delta) => {
    setCounters((prev) => {
      const cur = prev[key] || emptyCountState();
      const nextVal = Math.max(0, Math.round(Number(cur.value) || 0) + delta);
      const next = {
        ...prev,
        [key]: {
          ...cur,
          value: nextVal,
          dirty: true,
          error: null,
        },
      };
      broadcastRef.current(next);
      return next;
    });
  }, []);

  const handleReset = useCallback((key) => {
    setCounters((prev) => {
      const next = {
        ...prev,
        [key]: {
          ...(prev[key] || emptyCountState()),
          value: 0,
          dirty: true,
          error: null,
        },
      };
      broadcastRef.current(next);
      return next;
    });
  }, []);

  const handleSave = useCallback(
    async (player) => {
      const key = player.key;
      const cur = countersRef.current[key] || emptyCountState();
      const value = Math.max(0, Math.round(Number(cur.value) || 0));
      setCounters((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || emptyCountState()),
          saving: true,
          error: null,
        },
      }));
      try {
        const data = await saveTeCountRecord(teId, {
          userId: player.userId,
          playerId: player.playerId,
          count: value,
          countLabel: countLabel || null,
        });
        const saved = Number(data?.metric_value);
        setCounters((prev) => {
          const next = {
            ...prev,
            [key]: {
              ...(prev[key] || emptyCountState()),
              saving: false,
              dirty: false,
              value: Number.isFinite(saved) ? saved : value,
              savedValue: Number.isFinite(saved) ? saved : value,
            },
          };
          broadcastRef.current(next);
          return next;
        });
      } catch (err) {
        setCounters((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || emptyCountState()),
            saving: false,
            error: err?.message || t("error") || "Error",
          },
        }));
      }
    },
    [teId, t, countLabel]
  );

  const handleDelete = useCallback(
    async (player) => {
      const key = player.key;
      setCounters((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || emptyCountState()),
          deleting: true,
          error: null,
        },
      }));
      try {
        await deleteTeCountRecord(teId, {
          userId: player.userId,
          playerId: player.playerId,
        });
        setCounters((prev) => {
          const next = {
            ...prev,
            [key]: emptyCountState(),
          };
          broadcastRef.current(next);
          return next;
        });
      } catch (err) {
        setCounters((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || emptyCountState()),
            deleting: false,
            error: err?.message || t("error") || "Error",
          },
        }));
      }
    },
    [teId, t]
  );

  if (!open || typeof document === "undefined") return null;

  const portalRoot = getLiveTrainingPortalRoot() || document.body;
  const recordedCount = (players || []).filter(
    (p) => counters[p.key]?.savedValue != null
  ).length;

  const sheet = (
    <div
      className="lt-chrono-panel__backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="lt-chrono-panel lt-count-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-count-panel-title"
        style={{
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lt-chrono-panel__header">
          <div className="lt-chrono-panel__header-text">
            <p className="lt-chrono-panel__eyebrow">
              {eyebrow || t("liveTrainingChronoModeEyebrow") || "Off-table"}
            </p>
            <h2 id="lt-count-panel-title">
              <FormatListNumberedIcon fontSize="small" />
              {t("liveTrainingCountModeTitle") || "Count drills"}
            </h2>
            <p className="lt-chrono-panel__subtitle">
              {t("liveTrainingCountModeSubtitle", { label: drillTitle }) ||
                `Tap + / − to count ${drillTitle} for each player, then save.`}
            </p>
          </div>
          <button
            type="button"
            className="lt-chrono-panel__close"
            onClick={onClose}
            aria-label={t("close") || "Close"}
          >
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <div className="lt-chrono-panel__body">
        {stationKey ? (
          <div className="lt-chrono-panel__camera-bind">
            <label className="lt-chrono-panel__camera-label">
              <span>
                {t("liveTrainingChronoStationCamera") ||
                  "Camera for this station / group"}
              </span>
              <select
                className="lt-chrono-panel__camera-select"
                value={cameraDeviceId || ""}
                onChange={(e) => {
                  const id = e.target.value || null;
                  setCameraDeviceId(id);
                  setStationCameraDeviceId(stationKey, id);
                }}
              >
                <option value="">
                  {t("liveTrainingChronoCameraDefault") || "Default camera"}
                </option>
                {cameraDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || d.deviceId}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={`lt-chrono-panel__btn lt-chrono-panel__btn--link-cam${
                cameraDeviceId ? " lt-chrono-panel__btn--link-cam-active" : ""
              }`}
              onClick={() =>
                setStationCameraDeviceId(stationKey, cameraDeviceId)
              }
              disabled={!cameraDeviceId}
              title={
                t("liveTrainingChronoBindCamera") ||
                "Remember this camera for the station"
              }
            >
              <LinkIcon fontSize="small" />
              {cameraDeviceId
                ? t("liveTrainingChronoCameraLinked") || "Linked"
                : t("liveTrainingChronoBindCamera") || "Link to station"}
            </button>
          </div>
        ) : null}

        <p className="lt-count-panel__label-chip">{drillTitle}</p>

        {recordedCount > 0 ? (
          <p className="lt-chrono-panel__summary">
            {t("liveTrainingCountRecordedCount", { count: recordedCount }) ||
              `${recordedCount} recorded count${recordedCount === 1 ? "" : "s"}`}
          </p>
        ) : null}

        {loadError ? (
          <p className="lt-chrono-panel__error" role="alert">
            {loadError}
          </p>
        ) : null}
        {loading ? (
          <p className="lt-chrono-panel__hint">{t("loading") || "…"}</p>
        ) : null}

        {!players.length ? (
          <p className="lt-chrono-panel__empty">
            {t("liveTrainingChronoModeNoPlayers") ||
              "No present players in this group yet. Mark attendance first."}
          </p>
        ) : (
          <ol className="lt-chrono-panel__list">
            {players.map((player, index) => {
              const row = counters[player.key] || emptyCountState();
              return (
                <li
                  key={player.key}
                  className={`lt-chrono-panel__row${
                    row.dirty ? " lt-count-panel__row--dirty" : ""
                  }`}
                >
                  <div className="lt-chrono-panel__identity">
                    <span className="lt-chrono-panel__order">{index + 1}</span>
                    <div>
                      <div className="lt-chrono-panel__name">{player.name}</div>
                      <div className="lt-chrono-panel__meta">
                        {player.tableNumber != null
                          ? `${t("liveTrainingChronoTable") || "Table"} ${player.tableNumber}`
                          : t("liveTrainingChronoPresent") || "Present"}
                        {row.savedValue != null
                          ? ` · ${t("liveTrainingChronoSaved") || "Saved"} ${row.savedValue}`
                          : null}
                      </div>
                    </div>
                  </div>
                  <div className="lt-count-panel__value" aria-live="polite">
                    {row.value}
                  </div>
                  <div className="lt-chrono-panel__actions">
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-count-panel__btn--bump"
                      onClick={() => bump(player.key, -1)}
                      disabled={row.saving || row.deleting || row.value <= 0}
                      aria-label="-1"
                    >
                      <RemoveIcon fontSize="small" />
                    </button>
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-chrono-panel__btn--start"
                      onClick={() => bump(player.key, 1)}
                      disabled={row.saving || row.deleting}
                      aria-label="+1"
                    >
                      <AddIcon fontSize="small" />
                      +1
                    </button>
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-count-panel__btn--plus5"
                      onClick={() => bump(player.key, 5)}
                      disabled={row.saving || row.deleting}
                    >
                      +5
                    </button>
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-chrono-panel__btn--start lt-count-panel__btn--save"
                      onClick={() => handleSave(player)}
                      disabled={row.saving || row.deleting || !row.dirty}
                    >
                      <SaveOutlinedIcon fontSize="small" />
                      {t("save") || "Save"}
                    </button>
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-chrono-panel__btn--reset"
                      onClick={() => handleReset(player.key)}
                      disabled={row.saving || row.deleting}
                      title={t("liveTrainingChronoReset") || "Reset"}
                      aria-label={t("liveTrainingChronoReset") || "Reset"}
                    >
                      <RestartAltIcon fontSize="small" />
                    </button>
                    {row.savedValue != null ? (
                      <button
                        type="button"
                        className="lt-chrono-panel__btn lt-chrono-panel__btn--delete"
                        onClick={() => handleDelete(player)}
                        disabled={row.saving || row.deleting}
                        title={t("liveTrainingCountDelete") || "Delete count"}
                        aria-label={t("liveTrainingCountDelete") || "Delete count"}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    ) : null}
                  </div>
                  {row.error ? (
                    <p className="lt-chrono-panel__row-error" role="alert">
                      {row.error}
                    </p>
                  ) : null}
                  {row.saving ? (
                    <p className="lt-chrono-panel__row-hint">
                      {t("saving") || "Saving…"}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, portalRoot);
}

export default LiveTrainingCoachCountPanel;
